import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import CopyableId from '../common/CopyableId'; // Import the CopyableId component
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const ProgramVersions = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const navigate = useNavigate(); // Initialize navigate hook
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch versions data (wrapped in useCallback)
  const fetchVersions = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);
    setVersions([]); // Clear previous versions

    try {
      // First get the overview to get the rootId
      const overviewData = await apiService.getOverview(programId); // Use programId prop

      if (!overviewData || !overviewData.rootId) {
        // If rootId is missing, we might not be able to fetch versions.
        // Set versions to empty or handle as appropriate.
        console.warn('Root ID not found for this program, cannot fetch versions.');
        setVersions([]);
        // Optionally set an error or message: setError('Cannot fetch versions without a Root ID.');
        setIsLoading(false);
        return;
      }

      // Then fetch versions using the rootId
      const data = await apiService.getVersions(overviewData.rootId);
      setVersions(data || []); // Ensure data is an array
    } catch (err) {
      console.error('Failed to fetch program versions:', err);
      setError(t('programVersions.error'));
      setVersions([]); // Set to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [programId]); // Dependency on programId

  // Fetch data on mount or when programId changes
  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // Format date to a more readable format
  // Note: Date-only strings like "2012-06-20" are parsed as UTC midnight,
  // which can shift to the previous day when converted to local time.
  // We use UTC methods to avoid this timezone issue.
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  // Handle click on TMSID to load that program
  const handleTMSIDClick = (tmsId) => {
    if (tmsId) {
      navigate(`/program/${tmsId}`); // Use navigate
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500">
        <p>{error}</p>
        <button
          className="mt-2 px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
          onClick={fetchVersions} // Retry fetch
        >
          {t('programTitles.tryAgain')}
        </button>
      </div>
    );
  }

  if (!versions || versions.length === 0) { // Check if versions is null or empty
    return (
      <div className="p-6 text-center text-gray-500">
        <p>{t('programVersions.noVersions')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-auto h-full" style={{ maxHeight: 'calc(100vh - 300px)' }}>
      <h2 className="text-xl font-semibold mb-4 text-left">{t('programVersions.title')}</h2>
      <div className="overflow-x-auto overflow-y-auto">
        <table className="w-full bg-white border border-gray-200 text-left">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.tmsId')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.title')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.episodeTitle')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.language')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.descriptionLanguage')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.originalAudioLanguage')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.programType')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.subtype')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.runTime')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.colorCode')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programVersions.columns.updateDate')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-left">
            {versions.map((version, index) => (
              <tr key={version.TMSId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {/* Pass handleTMSIDClick to onValueClick, remove wrapper span */}
                  <CopyableId 
                    value={version.TMSId} 
                    onValueClick={() => handleTMSIDClick(version.TMSId)} 
                  /> 
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.title || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.episode_title || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.lang || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.desc_lang || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.origAudioLang || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.progType || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.subType || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.runTime || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {version.colorCode || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatDate(version.updateDate)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-between text-xs text-gray-500 px-4 pb-4">
        <div>
          {t('programVersions.showing', { count: versions.length })}
        </div>
        <div>
          <button
            className={`px-3 py-1 border border-gray-300 rounded-sm ${versions.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            onClick={() => exportToCsv(versions, `${programId}_versions.csv`)}
            disabled={versions.length === 0}
          >
            {t('programVersions.export')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramVersions;
